export default class WebRtc {
	static getUserMedia(constraints = { audio: true, video: true }) {
		if (typeof navigator.mediaDevices === "undefined" || typeof navigator.mediaDevices.getUserMedia !== "function") {
			const error = new Error("WebRTC is not supported by this browser");
			error.name = "NotSupportedError";

			return Promise.reject(error);
		}

		return navigator.mediaDevices.getUserMedia(constraints)
		.catch((error) => Promise.reject(WebRtc.handleMediaError(error)));
	}

	static getDisplayMedia(constraints = { video: true }) {
		if (typeof navigator.mediaDevices === "undefined" || typeof navigator.mediaDevices.getDisplayMedia !== "function") {
			const error = new Error("Screen sharing is not supported by this browser");
			error.name = "NotSupportedError";

			return Promise.reject(error);
		}

		return navigator.mediaDevices.getDisplayMedia(constraints)
		.catch((error) => Promise.reject(WebRtc.handleMediaError(error)));
	}

	static handleMediaError(error) {
		console.log(error.name, error.message);

		// https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#Exceptions
		// https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getDisplayMedia#Exceptions

		const errorName = error.name;

		switch (error.name) {
			case "AbortError":
				error = new Error("A problem occurred that prevents the device from being used");
				break;
			case "InvalidStateError": // only for getDisplayMedia()
				break;
			case "NotAllowedError":
				error = new Error("Permissions have not been granted to use your camera, microphone or desktop.");
				break;
			case "NotFoundError":
				error = new Error("No media capture device(s) found.");
				break;
			case "NotReadableError":
				error = new Error("Media capture device(s) is either blocked or already in use by another process.");
				break;
			case "OverconstrainedError":
				break;
			case "SecurityError": // only for getUserMedia()
				break;
			case "TypeError":
				break;
		}

		error.name = errorName;

		return error;
	}

	static getDevices() {
		return navigator.mediaDevices.enumerateDevices()
		.then((devices) => {
			return {
				// devices with deviceId === "default" can be removed as they are always duplicates of another deviceId
				audioInputs: devices.filter((device) => device.kind === "audioinput" && device.deviceId !== "default"),
				audioOutputs: devices.filter((device) => device.kind === "audiooutput" && device.deviceId !== "default"),
				videoInputs: devices.filter((device) => device.kind === "videoinput")
			};
		});
	}

	replaceTracks(stream) {
		if (!this.peerConnection)
			return;

		stream.getTracks().forEach((track) => {
			const sender = this.peerConnection.getSenders().find((s) => s.track.kind === track.kind);

			if (typeof sender !== "undefined")
				sender.replaceTrack(track);
		});
	}

	start(constraints) {
		if (this.webSocket)
			return Promise.reject(new Error("Start not permitted while connected"));

		this.stop();

		return WebRtc.getUserMedia(constraints).then((stream) => {
			this.localStream = stream;

			return stream;
		});
	}

	stop() {
		if (!this.localStream)
			return;

		this.localStream.getTracks().forEach((track) => track.stop());
		this.localStream = null;
	}

	connect(config) {
		return this._connect(config).catch((error) => {
			this.close();

			return Promise.reject(error);
		});
	}

	_connect(config) {
		if (!this.localStream)
			return Promise.reject(new Error("Local stream not running"));

		if (this.webSocket)
			return Promise.reject(new Error("Already connected"));

		const url = `wss://ws.mnsbone.net/webrtc/connect?uri=${encodeURIComponent(config.uri)}&name=${encodeURIComponent(config.fromName)}&furi=${encodeURIComponent(config.fromUri)}`;

		return new Promise((resolve, reject) => {
			const peerConnection = new RTCPeerConnection({ iceServers: config.iceServers });
			const webSocket = new WebSocket(url);

			this.peerConnection = peerConnection;
			this.webSocket = webSocket;

			webSocket.onopen = () => {
				console.info("WebSocket connected");

				peerConnection.createOffer().then((offer) => peerConnection.setLocalDescription(offer))
				.catch((error) => {
					reject(new Error("Unable to create offer: " + error.toString()));
				});
			};

			webSocket.onmessage = (event) => {
				const msg = event.data;

				if (msg.length < 1 || msg.charAt(0) !== "A")
					return;

				const desc = { sdp: msg.substr(1), type: "answer" };

				desc.sdp = desc.sdp.replace(/a=rtcp-fb:\* nack pli[ ]*\r\n/g, "a=rtcp-fb:* nack pli\r\na=rtcp-fb:* nack\r\n");

				if (navigator.userAgent.includes("Firefox"))
					desc.sdp = desc.sdp.replace(/b=TIAS:[0-9]+\r\n/, "b=TIAS:" + config.bandwidth * 1000 + "\r\n");
				else
					desc.sdp = desc.sdp.replace(/b=TIAS:[0-9]+\r\n/, "b=AS:" + config.bandwidth + "\r\n");

				peerConnection.setRemoteDescription(desc);

				console.info("Received answer");
				// console.log(desc.sdp);
			};

			webSocket.onerror = (error) => {
				reject(new Error(`Unable to establish websocket connection: ${error}`));
			};

			webSocket.onclose = (event) => {
				console.info(`WebSocket closed: ${event.reason || event.code}`);
				this.close();
			};

			peerConnection.onicegatheringstatechange = () => {
				if (peerConnection.iceGatheringState !== "complete")
					return;

				const bw = "b=CT:" + config.bandwidth + "\r\nt=0 0\r\n";

				let sdp = peerConnection.localDescription.sdp;

				sdp = sdp.replace(/a=candidate:([0-9a-zA-Z.: ]+) generation [0-9]+[0-9a-zA-Z\- ]*\r\n/g, "a=candidate:$1\r\n");
				sdp = sdp.replace(/a=candidate:([0-9a-zA-Z.: ]+) tcptype active[ ]*\r\n/g, "a=candidate:$1\r\n");
				sdp = sdp.replace(/a=rtcp-fb:([0-9]+) nack[ ]*\r\n/g, "a=rtcp-fb:$1 nack\r\na=rtcp-fb:* nack pli\r\n");
				sdp = sdp.replace(/t=0 0\r\n/, bw);

				webSocket.send("O" + sdp);

				console.info("Sending offer");
				// console.log(sdp);
			};

			peerConnection.ontrack = (event) => resolve(event.streams[0]);

			this.localStream.getTracks().forEach((track) => peerConnection.addTrack(track, this.localStream));
		});
	}

	muteAudioTrack(val) {
		if (this.localStream) {
			this.localStream.getAudioTracks().forEach((track) => {
				track.enabled = !val;
			});

			return this.localStream.getAudioTracks().every((track) => !track.enabled);
		}

		return true;
	}

	muteVideoTrack(val) {
		if (this.localStream) {
			this.localStream.getVideoTracks().forEach((track) => {
				track.enabled = !val;
			});

			return this.localStream.getVideoTracks().every((track) => !track.enabled);
		}

		return true;
	}

	sendDtmf(tones, duration = 500, interToneGap = 70) {
		if (!this.peerConnection)
			return;

		const re = /^[A-D0-9#*]+$/;
		const sender = this.peerConnection.getSenders().find((s) => s.track.kind === "audio");

		if (re.test(tones) && sender && sender.dtmf)
			sender.dtmf.insertDTMF(tones, duration, interToneGap);
	}

	close() {
		if (this.peerConnection) {
			this.peerConnection.close();
			this.peerConnection = null;
		}

		if (this.webSocket) {
			this.webSocket.close();
			this.webSocket = null;
		}
	}

	destroy() {
		this.close();
		this.stop();
	}
}
